import React, { useState, useEffect } from 'react';
import { X, Check, AlertCircle, Loader2 } from 'lucide-react';

const ManualOverridePanel = ({ isOpen, onClose, plan, onCommit, isLoading }) => {
    const [overrides, setOverrides] = useState([]);
    const [wineDeductions, setWineDeductions] = useState([]);

    // Extract all unique ingredients from the plan's physical_deductions
    useEffect(() => {
        if (plan && plan.meals) {
            // 1. Handle Food Ingredients
            const allDeductions = [];
            plan.meals.forEach(meal => {
                if (meal.physical_deductions) {
                    meal.physical_deductions.forEach(pd => {
                        const existing = allDeductions.find(d => d.name === pd.name && d.unit === pd.unit);
                        if (existing) {
                            existing.qty += pd.qty;
                        } else {
                            allDeductions.push({ ...pd });
                        }
                    });
                }
            });
            setOverrides(allDeductions.map(d => ({ ...d, originalQty: d.qty })));

            // 2. Handle Wine Pairings (V6-Quantity)
            const pairings = [];
            plan.meals.forEach(meal => {
                const pairing = meal.wine_pairing;
                if (pairing && pairing.id) {
                    // Avoid duplicates if the same bottle is recommended for multiple meals
                    if (!pairings.find(p => p.wine.id === pairing.id)) {
                        pairings.push({ wine: pairing, deduct: true });
                    }
                }
            });
            setWineDeductions(pairings);
        }
    }, [plan, isOpen]);

    if (!isOpen) return null;

    const handleQtyChange = (idx, val) => {
        const newOverrides = [...overrides];
        newOverrides[idx].qty = parseFloat(val) || 0;
        setOverrides(newOverrides);
    };

    const toggleWineDeduction = (idx) => {
        const newWines = [...wineDeductions];
        newWines[idx].deduct = !newWines[idx].deduct;
        setWineDeductions(newWines);
    };

    const handleCommit = () => {
        const foodOverrides = overrides.map(({ name, qty, unit }) => ({ name, qty, unit }));
        const skipWineIds = wineDeductions
            .filter(wd => !wd.deduct)
            .map(wd => parseInt(wd.wine.id));

        onCommit(foodOverrides, skipWineIds);
    };

    return (
        <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} />

            <div className="relative w-full max-w-2xl bg-[var(--glass-surface)] border border-[var(--glass-border)] rounded-t-[2.5rem] sm:rounded-[2.5rem] shadow-2xl overflow-hidden animate-enter h-[85vh] sm:h-auto max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="p-8 border-b border-[var(--glass-border)] flex items-center justify-between">
                    <div>
                        <h3 className="text-2xl font-black text-[var(--text-primary)] mb-1 flex items-center gap-2">
                            Inventory Adjustment
                        </h3>
                        <p className="text-[var(--text-secondary)] text-sm font-medium">Fine-tune exactly how much of each ingredient was used.</p>
                    </div>
                    <button onClick={onClose} className="p-3 hover:bg-[var(--text-secondary)]/10 rounded-2xl transition-all">
                        <X className="w-6 h-6 text-[var(--text-secondary)]" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-8 no-scrollbar">
                    <div className="grid grid-cols-1 gap-4">
                        {overrides.length > 0 ? (
                            <>
                                <div className="text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-[0.2em] mb-2 px-1">Pantry Ingredients</div>
                                {overrides.map((item, idx) => (
                                    <div key={idx} className="glass-panel p-4 rounded-3xl flex items-center justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="text-sm font-black text-[var(--text-primary)] capitalize mb-1">{item.name}</div>
                                            <div className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-widest opacity-60">Estimated: {item.originalQty} {item.unit}</div>
                                        </div>

                                        <div className="flex items-center gap-2 bg-[var(--bg-secondary)] p-1 rounded-2xl border border-[var(--glass-border)]">
                                            <input
                                                type="number"
                                                value={item.qty}
                                                onChange={(e) => handleQtyChange(idx, e.target.value)}
                                                className="w-20 bg-transparent border-none text-right font-black text-[var(--text-primary)] focus:ring-0 px-2"
                                                step="0.01"
                                            />
                                            <span className="pr-3 text-xs font-bold text-[var(--text-secondary)]">{item.unit}</span>
                                        </div>
                                    </div>
                                ))}
                            </>
                        ) : (
                            <div className="py-10 text-center">
                                <AlertCircle className="w-12 h-12 text-[var(--text-secondary)]/20 mx-auto mb-4" />
                                <p className="text-[var(--text-secondary)] font-medium italic">No precise pantry quantities detected.</p>
                            </div>
                        )}

                        {wineDeductions.length > 0 && (
                            <>
                                <div className="mt-6 text-[10px] font-black text-[var(--text-secondary)] uppercase tracking-[0.2em] mb-2 px-1">Cellar Deductions</div>
                                {wineDeductions.map((wd, idx) => (
                                    <div
                                        key={`wine-${idx}`}
                                        onClick={() => toggleWineDeduction(idx)}
                                        className={`glass-panel p-4 rounded-3xl flex items-center justify-between gap-4 cursor-pointer transition-all ${wd.deduct ? 'ring-1 ring-[var(--accent-color)]/30' : 'opacity-60'}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`p-3 rounded-2xl ${wd.deduct ? 'bg-[var(--accent-color)]/10 text-[var(--accent-color)]' : 'bg-[var(--text-secondary)]/10 text-[var(--text-secondary)]'}`}>
                                                <div className="w-5 h-5 flex items-center justify-center font-black">W</div>
                                            </div>
                                            <div className="flex-1">
                                                <div className="text-sm font-black text-[var(--text-primary)]">{wd.wine.name}</div>
                                                <div className="text-[10px] font-bold text-[var(--text-secondary)] uppercase tracking-widest opacity-60">
                                                    {wd.wine.vintage} {wd.wine.varietal} {wd.wine.region ? `• ${wd.wine.region}` : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${wd.deduct ? 'bg-[var(--accent-color)] border-[var(--accent-color)] text-white' : 'border-[var(--glass-border)]'}`}>
                                            {wd.deduct && <Check className="w-4 h-4" />}
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-8 bg-[var(--text-secondary)]/5 border-t border-[var(--glass-border)]">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <button
                            onClick={onClose}
                            className="flex-1 px-8 py-4 rounded-2xl font-bold text-[var(--text-secondary)] hover:bg-[var(--text-secondary)]/10 transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCommit}
                            disabled={isLoading}
                            className="flex-[2] bg-[var(--accent-color)] hover:brightness-110 text-white px-8 py-4 rounded-2xl font-black text-sm tracking-widest uppercase shadow-xl shadow-blue-500/20 transition-all active:scale-95 flex items-center justify-center gap-2"
                        >
                            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                            Commit Final Quantities
                        </button>
                    </div>
                    <div className="mt-6 flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 p-4 rounded-2xl">
                        <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                        <p className="text-[10px] font-bold text-amber-600 leading-relaxed uppercase tracking-wider">
                            Committing will deduct these quantities from your kitchen and lock the meal plan against further changes.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManualOverridePanel;
